import React from "react";
import { Document, Image, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { compareAccountNames } from "../../util";
import valleyLogo from "../../assets/images/valley-irrigation-logo.png";

const styles = StyleSheet.create({
  page: {
    padding: "40pt 48pt 60pt 48pt",
    fontFamily: "Helvetica",
    fontSize: 9
  },
  /* ─── Header ─── */
  header: {
    marginBottom: 24
  },
  brandRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 28
  },
  logo: {
    width: 120,
    height: "auto"
  },
  metaRight: {
    textAlign: "right"
  },
  metaLabel: {
    fontSize: 7,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 1
  },
  metaValue: {
    fontSize: 9,
    color: "#374151",
    marginBottom: 4
  },
  titleRow: {
    marginBottom: 16
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#111827",
    marginBottom: 2
  },
  subtitle: {
    fontSize: 9,
    color: "#6b7280"
  },
  totalsRow: {
    alignItems: "flex-end",
    paddingTop: 12,
    paddingBottom: 12,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb"
  },
  totalLine: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "baseline",
    marginBottom: 4
  },
  totalLineLast: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "baseline"
  },
  totalLabel: {
    fontSize: 8,
    color: "#9ca3af",
    marginRight: 10,
    width: 110,
    textAlign: "right"
  },
  totalValue: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#111827",
    width: 90,
    textAlign: "right"
  },
  totalValueGreen: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#10b981",
    width: 90,
    textAlign: "right"
  },
  /* ─── Summary Table ─── */
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 8,
    marginTop: 20
  },
  summaryHeader: {
    flexDirection: "row",
    backgroundColor: "#f9fafb",
    borderRadius: 2,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 2
  },
  summaryRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: "#f3f4f6"
  },
  summaryCell: {
    fontSize: 8.5
  },
  summaryCellBold: {
    fontSize: 8.5,
    fontWeight: "bold"
  },
  summaryHeaderCell: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.3
  },
  /* ─── Detail Sections ─── */
  parentSection: {
    marginTop: 18
  },
  parentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 7,
    paddingHorizontal: 8,
    backgroundColor: "#f3f4f6",
    borderRadius: 2,
    marginBottom: 4
  },
  parentName: {
    fontSize: 9.5,
    fontWeight: "bold",
    color: "#1f2937"
  },
  rewardCaption: {
    flexDirection: "row",
    alignItems: "baseline",
    marginTop: 1.5
  },
  rewardCaptionLabel: {
    fontSize: 5.5,
    fontWeight: "bold",
    color: "#9ca3af",
    letterSpacing: 0.4,
    marginRight: 4
  },
  rewardCaptionValue: {
    fontSize: 7,
    fontFamily: "Courier",
    color: "#6b7280"
  },
  parentTotalsBlock: {
    alignItems: "flex-end"
  },
  parentTotalLine: {
    flexDirection: "row",
    alignItems: "baseline",
    marginBottom: 1
  },
  parentTotalLabel: {
    fontSize: 7,
    color: "#9ca3af",
    marginRight: 6,
    width: 50,
    textAlign: "right"
  },
  parentTotalValue: {
    fontSize: 7.5,
    color: "#374151",
    width: 60,
    textAlign: "right"
  },
  parentTotalValueGreen: {
    fontSize: 7.5,
    color: "#10b981",
    width: 60,
    textAlign: "right"
  },
  childHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    paddingLeft: 16,
    paddingRight: 8,
    marginTop: 4,
    marginBottom: 2
  },
  childName: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: "#374151"
  },
  childNameBlock: {
    flexDirection: "column"
  },
  childMeta: {
    fontSize: 7.5,
    color: "#9ca3af",
    marginLeft: 8
  },
  invoiceTableHeader: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingLeft: 24,
    paddingRight: 8,
    marginBottom: 1
  },
  invoiceTableHeaderCell: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 0.3
  },
  invoiceRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingLeft: 24,
    paddingRight: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: "#f3f4f6"
  },
  invoiceCell: {
    fontSize: 8
  },
  invoiceCellBold: {
    fontSize: 8,
    fontWeight: "bold"
  },
  childSubtotalBlock: {
    alignItems: "flex-end",
    paddingVertical: 6,
    paddingLeft: 24,
    paddingRight: 8,
    marginTop: 6,
    marginBottom: 14
  },
  childSubtotalLine: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "baseline",
    marginBottom: 2
  },
  childSubtotalLabel: {
    fontSize: 7.5,
    color: "#9ca3af",
    marginRight: 8,
    width: 60,
    textAlign: "right"
  },
  childSubtotalValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#374151",
    width: 70,
    textAlign: "right"
  },
  /* ─── Footer ─── */
  footer: {
    marginTop: 24,
    paddingTop: 12,
    borderTopWidth: 1.5,
    borderTopColor: "#e5e7eb",
    alignItems: "flex-end"
  },
  footerLine: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "baseline",
    marginBottom: 3
  },
  footerLabel: {
    fontSize: 8,
    color: "#9ca3af",
    marginRight: 10,
    width: 110,
    textAlign: "right"
  },
  footerValue: {
    fontSize: 11,
    fontWeight: "bold",
    width: 90,
    textAlign: "right"
  },
  footerValueGreen: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#10b981",
    width: 90,
    textAlign: "right"
  },
  /* ─── Copyright ─── */
  copyright: {
    position: "absolute",
    bottom: 32,
    left: 48,
    right: 48,
    paddingTop: 8,
    borderTopWidth: 0.5,
    borderTopColor: "#e5e7eb"
  },
  copyrightText: {
    fontSize: 7,
    color: "#9ca3af",
    textAlign: "center"
  }
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2
  }).format(value || 0);

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatGeneratedDate = () => {
  return new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

// Invoice table column definitions. Widths are relative weights that get
// normalized to 100% across whichever columns are actually visible, so hiding
// a column reflows the remaining ones instead of leaving a gap.
const INVOICE_COLUMN_META = {
  invoiceNumber: { label: "Invoice #", weight: 22 },
  dealerName: { label: "Dealer", weight: 28 },
  invoiceDate: { label: "Date", weight: 18 },
  invoiceTotal: { label: "Total", weight: 16 },
  rebateAmount: { label: "Rebate", weight: 16 }
};

const INVOICE_COLUMN_ORDER = ["invoiceNumber", "dealerName", "invoiceDate", "invoiceTotal", "rebateAmount"];

const renderInvoiceCellValue = (key, inv) => {
  switch (key) {
    case "invoiceDate": return formatDate(inv.invoiceDate);
    case "invoiceTotal": return formatCurrency(inv.invoiceTotal);
    case "rebateAmount": return formatCurrency(inv.rebateAmount);
    default: return inv[key];
  }
};

/**
 * Groups flat invoice list into hierarchy:
 * parentAccount → childFarms → invoices
 */
const groupInvoices = (invoices) => {
  const parents = {};
  invoices.forEach((inv) => {
    const parentKey = inv.parentAccount || inv.farmName || "Ungrouped";
    const childKey = inv.farmName || "Direct";
    if (!parents[parentKey]) parents[parentKey] = { children: {} };
    if (!parents[parentKey].children[childKey]) parents[parentKey].children[childKey] = [];
    parents[parentKey].children[childKey].push(inv);
  });
  return parents;
};

const StatementPdf = ({ monthLabel, visibleColumns, visibleInvoices, grandTotal, grandRebate, reference }) => {
  const grouped = groupInvoices(visibleInvoices);
  const generatedDate = formatGeneratedDate();

  // Sort parents A–Z (letters before numbers/symbols) so the rendered and
  // downloaded statement order matches the sorted account picker.
  const sortedGroupEntries = Object.entries(grouped).sort(([a], [b]) =>
    compareAccountNames(a, b)
  );

  // Resolve which invoice columns to render (canonical order), falling back to
  // all columns when none are provided. Widths are normalized across these.
  const requestedKeys = (visibleColumns || []).map((col) => col.key);
  const activeColumnKeys = INVOICE_COLUMN_ORDER.filter((key) =>
    requestedKeys.length === 0 ? true : requestedKeys.includes(key)
  );
  const totalColumnWeight =
    activeColumnKeys.reduce((sum, key) => sum + INVOICE_COLUMN_META[key].weight, 0) || 1;
  const columnWidth = (key) => `${(INVOICE_COLUMN_META[key].weight / totalColumnWeight) * 100}%`;

  // Build parent summaries
  const parentSummaries = sortedGroupEntries.map(([parentName, { children }]) => {
    const childEntries = Object.entries(children);
    const invoiceCount = childEntries.reduce((sum, [, invs]) => sum + invs.length, 0);
    const invoiceTotal = childEntries.reduce((sum, [, invs]) => sum + invs.reduce((s, inv) => s + (inv.invoiceTotal || 0), 0), 0);
    const rebateTotal = childEntries.reduce((sum, [, invs]) => sum + invs.reduce((s, inv) => s + (inv.rebateAmount || 0), 0), 0);
    const rewardId = childEntries.flatMap(([, invs]) => invs).find((inv) => inv.parentRewardsCode)?.parentRewardsCode || null;
    return { parentName, rewardId, childCount: childEntries.length, invoiceCount, invoiceTotal, rebateTotal };
  });

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>

        {/* ═══════ HEADER / GRAND SUMMARY ═══════ */}
        <View style={styles.header}>
          <View style={styles.brandRow}>
            <Image style={styles.logo} src={valleyLogo} />
            <View style={styles.metaRight}>
              {reference ? (
                <>
                  <Text style={styles.metaLabel}>Reference</Text>
                  <Text style={styles.metaValue}>{reference}</Text>
                </>
              ) : null}
              <Text style={styles.metaLabel}>Generated</Text>
              <Text style={styles.metaValue}>{generatedDate}</Text>
            </View>
          </View>

          <View style={styles.titleRow}>
            <Text style={styles.title}>Rebate Statement</Text>
            <Text style={styles.subtitle}>Selected period: {monthLabel}</Text>
          </View>

          <View style={styles.totalsRow}>
            <View style={styles.totalLine}>
              <Text style={styles.totalLabel}>Total Invoice Amount</Text>
              <Text style={styles.totalValue}>{formatCurrency(grandTotal)}</Text>
            </View>
            <View style={styles.totalLineLast}>
              <Text style={styles.totalLabel}>Total Rebate Amount</Text>
              <Text style={styles.totalValueGreen}>{formatCurrency(grandRebate)}</Text>
            </View>
          </View>
        </View>

        {/* ═══════ PARENT ACCOUNT SUMMARY TABLE ═══════ */}
        <Text style={styles.sectionTitle}>Account Summary</Text>
        <View style={styles.summaryHeader}>
          <Text style={[styles.summaryHeaderCell, { width: "28%" }]}>Parent Account</Text>
          <Text style={[styles.summaryHeaderCell, { width: "20%" }]}>Rewards ID</Text>
          <Text style={[styles.summaryHeaderCell, { width: "10%" }]}>Farms</Text>
          <Text style={[styles.summaryHeaderCell, { width: "10%" }]}>Invoices</Text>
          <Text style={[styles.summaryHeaderCell, { width: "16%" }]}>Invoice Total</Text>
          <Text style={[styles.summaryHeaderCell, { width: "16%" }]}>Rebate Total</Text>
        </View>
        {parentSummaries.map((row) => (
          <View key={row.parentName} style={styles.summaryRow}>
            <Text style={[styles.summaryCellBold, { width: "28%" }]}>{row.parentName}</Text>
            <Text style={[styles.summaryCell, { width: "20%", fontFamily: "Courier", color: row.rewardId ? "#6b7280" : "#d1d5db" }]}>{row.rewardId || "\u2014"}</Text>
            <Text style={[styles.summaryCell, { width: "10%" }]}>{row.childCount}</Text>
            <Text style={[styles.summaryCell, { width: "10%" }]}>{row.invoiceCount}</Text>
            <Text style={[styles.summaryCellBold, { width: "16%" }]}>{formatCurrency(row.invoiceTotal)}</Text>
            <Text style={[styles.summaryCellBold, { width: "16%", color: "#10b981" }]}>{formatCurrency(row.rebateTotal)}</Text>
          </View>
        ))}

        {/* ═══════ DETAILED GROUPED SECTIONS ═══════ */}
        <Text style={[styles.sectionTitle, { marginTop: 44 }]}>Detail by Account</Text>
        {sortedGroupEntries.map(([parentName, { children }]) => {
          const groupInvs = Object.values(children).flat();
          const parentInvTotal = groupInvs.reduce((s, inv) => s + (inv.invoiceTotal || 0), 0);
          const parentRebTotal = groupInvs.reduce((s, inv) => s + (inv.rebateAmount || 0), 0);
          const parentReward = groupInvs.find((inv) => inv.parentRewardsCode)?.parentRewardsCode || null;

          return (
            <View key={parentName} style={styles.parentSection}>
              {/* Parent header */}
              <View style={styles.parentHeader} wrap={false}>
                <View>
                  <Text style={styles.parentName}>{parentName}</Text>
                  {parentReward ? (
                    <View style={styles.rewardCaption}>
                      <Text style={styles.rewardCaptionLabel}>REWARDS ID</Text>
                      <Text style={styles.rewardCaptionValue}>{parentReward}</Text>
                    </View>
                  ) : null}
                </View>
                <View style={styles.parentTotalsBlock}>
                  <View style={styles.parentTotalLine}>
                    <Text style={styles.parentTotalLabel}>Invoice Total</Text>
                    <Text style={styles.parentTotalValue}>{formatCurrency(parentInvTotal)}</Text>
                  </View>
                  <View style={styles.parentTotalLine}>
                    <Text style={styles.parentTotalLabel}>Rebate</Text>
                    <Text style={styles.parentTotalValueGreen}>{formatCurrency(parentRebTotal)}</Text>
                  </View>
                </View>
              </View>

              {/* Child farms */}
              {Object.entries(children).map(([childName, invoices]) => {
                const childInvTotal = invoices.reduce((s, inv) => s + (inv.invoiceTotal || 0), 0);
                const childRebTotal = invoices.reduce((s, inv) => s + (inv.rebateAmount || 0), 0);
                const childReward = invoices.find((inv) => inv.farmRewardsCode)?.farmRewardsCode || null;

                return (
                  <View key={childName}>
                    {/* Child label */}
                    <View style={styles.childHeader}>
                      <View style={styles.childNameBlock}>
                        <Text style={styles.childName}>{childName}</Text>
                        {childReward ? (
                          <View style={styles.rewardCaption}>
                            <Text style={styles.rewardCaptionLabel}>REWARDS ID</Text>
                            <Text style={styles.rewardCaptionValue}>{childReward}</Text>
                          </View>
                        ) : null}
                      </View>
                      <Text style={styles.childMeta}>
                        {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
                      </Text>
                    </View>

                    {/* Invoice column headers */}
                    <View style={styles.invoiceTableHeader}>
                      {activeColumnKeys.map((key) => (
                        <Text key={key} style={[styles.invoiceTableHeaderCell, { width: columnWidth(key) }]}>
                          {INVOICE_COLUMN_META[key].label}
                        </Text>
                      ))}
                    </View>

                    {/* Invoice rows */}
                    {invoices.map((inv) => (
                      <View key={inv.id} style={styles.invoiceRow}>
                        {activeColumnKeys.map((key) => {
                          const isMoney = key === "invoiceTotal" || key === "rebateAmount";
                          const cellStyle = isMoney ? styles.invoiceCellBold : styles.invoiceCell;
                          const extra = key === "rebateAmount" ? { color: "#10b981" } : null;
                          return (
                            <Text key={key} style={[cellStyle, { width: columnWidth(key) }, extra]}>
                              {renderInvoiceCellValue(key, inv)}
                            </Text>
                          );
                        })}
                      </View>
                    ))}

                    {/* Child subtotal */}
                    <View style={styles.childSubtotalBlock} wrap={false}>
                      <View style={styles.childSubtotalLine}>
                        <Text style={styles.childSubtotalLabel}>Subtotal:</Text>
                        <Text style={styles.childSubtotalValue}>{formatCurrency(childInvTotal)}</Text>
                      </View>
                      <View style={styles.childSubtotalLine}>
                        <Text style={styles.childSubtotalLabel}>Rebate:</Text>
                        <Text style={[styles.childSubtotalValue, { color: "#10b981" }]}>{formatCurrency(childRebTotal)}</Text>
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          );
        })}

        {/* ═══════ FOOTER GRAND TOTALS ═══════ */}
        <View style={styles.footer} wrap={false}>
          <View style={styles.footerLine}>
            <Text style={styles.footerLabel}>Grand Invoice Total</Text>
            <Text style={styles.footerValue}>{formatCurrency(grandTotal)}</Text>
          </View>
          <View style={styles.footerLine}>
            <Text style={styles.footerLabel}>Grand Rebate Total</Text>
            <Text style={styles.footerValueGreen}>{formatCurrency(grandRebate)}</Text>
          </View>
        </View>

        {/* ═══════ COPYRIGHT ═══════ */}
        <View style={styles.copyright}>
          <Text style={styles.copyrightText}>{"\u00A9"} 2026 Valmont Industries, Inc.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default StatementPdf;
